import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Camera, CameraResultType, CameraSource, CameraDirection } from '@capacitor/camera';
import { Device } from '@capacitor/device';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { X, CheckCircle, RotateCcw, Play, Square, Camera as CameraIcon, Move, Target } from 'lucide-react';
import { 
  ARRoomScan, 
  ARScanState, 
  ARScanSettings, 
  ARWallMeasurement, 
  ARScanPoint,
  Point,
  Point3D,
  Wall
} from '../types';
import { v4 as uuidv4 } from 'uuid';

interface ARRoomScannerProps {
  onScanComplete: (scan: ARRoomScan) => void;
  onClose: () => void;
  onIntegrateToFloorPlan: (scan: ARRoomScan) => void;
}

const ARRoomScanner: React.FC<ARRoomScannerProps> = ({
  onScanComplete,
  onClose,
  onIntegrateToFloorPlan
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const overlayCanvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [scanState, setScanState] = useState<ARScanState>({
    isScanning: false,
    currentScanId: null,
    scanProgress: 0,
    currentInstruction: 'Position your device and start scanning',
    detectedFeatures: {
      walls: [],
      corners: [],
      doors: [],
      windows: []
    },
    calibrationData: {
      deviceHeight: 1.5, // Default 1.5m from floor
      cameraAngle: 0,
      roomScale: 100 // pixels per meter
    }
  });

  const [settings, setSettings] = useState<ARScanSettings>({
    enableRealTimeOverlay: true,
    showMeasurements: true,
    autoDetectFeatures: true,
    scanSensitivity: 70,
    measurementUnit: 'metric'
  });

  const [currentScan, setCurrentScan] = useState<ARRoomScan | null>(null);
  const [isCalibrating, setIsCalibrating] = useState(false);
  const [deviceInfo, setDeviceInfo] = useState<any>(null);

  // Initialize camera stream
  const initializeCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'environment', // Use back camera
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      // Fallback to Capacitor Camera API
      try {
        const image = await Camera.getPhoto({
          quality: 90,
          allowEditing: false,
          resultType: CameraResultType.Uri,
          source: CameraSource.Camera,
          direction: CameraDirection.Rear
        });
        // Handle fallback camera mode
      } catch (capacitorError) {
        console.error('Capacitor camera error:', capacitorError);
      }
    }
  }, []);

  // Get device information for calibration
  const getDeviceInfo = useCallback(async () => {
    const info = await Device.getInfo();
    setDeviceInfo(info);
  }, []);

  // Start scanning process
  const startScanning = useCallback(() => {
    const scanId = uuidv4();
    setScanState(prev => ({
      ...prev,
      isScanning: true,
      currentScanId: scanId,
      scanProgress: 0,
      currentInstruction: 'Point camera at the first corner of the room'
    }));

    setCurrentScan({
      id: scanId,
      name: `Room Scan ${new Date().toLocaleTimeString()}`,
      walls: [],
      corners: [],
      doors: [],
      windows: [],
      floorArea: 0,
      ceilingHeight: 2.4, // Default ceiling height
      scanDate: new Date(),
      confidence: 0
    });
  }, []);

  // Stop scanning
  const stopScanning = useCallback(() => {
    setScanState(prev => ({
      ...prev,
      isScanning: false,
      currentInstruction: 'Scan completed. Review your measurements.'
    }));
  }, []);

  // Add measurement point with improved coordinate conversion
  const addMeasurementPoint = useCallback((screenPoint: Point) => {
    if (!scanState.isScanning || !currentScan) return;

    // Get canvas dimensions for proper coordinate mapping
    const canvas = overlayCanvasRef.current;
    if (!canvas) return;

    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;

    // Convert screen coordinates to normalized coordinates (0-1)
    const normalizedX = screenPoint.x / canvasWidth;
    const normalizedY = screenPoint.y / canvasHeight;

    // Enhanced coordinate conversion with proper scaling
    // Use the calibrated room scale and device height for more accurate mapping
    const roomScaleMeters = (scanState.calibrationData.roomScale / 100) * 6; // Convert scale to actual room size
    const deviceHeight = scanState.calibrationData.deviceHeight;
    
    // Map screen coordinates to world coordinates with perspective correction
    // Account for the fact that camera view represents a much larger area than initially assumed
    const perspectiveScale = 3.0; // Increase scale factor significantly
    const actualRoomSize = roomScaleMeters * perspectiveScale;
    
    const worldCoord: Point3D = {
      x: (normalizedX - 0.5) * actualRoomSize, // Center at 0, scale by actual room size
      y: deviceHeight, // Use calibrated device height
      z: (0.5 - normalizedY) * actualRoomSize // Invert Y and center at 0, scale by actual room size
    };

    const scanPoint: ARScanPoint = {
      id: uuidv4(),
      position: screenPoint,
      worldCoordinate: worldCoord,
      timestamp: Date.now(),
      confidence: 0.8 + Math.random() * 0.2
    };

    // Add haptic feedback when corner is detected
    try {
      Haptics.impact({ style: ImpactStyle.Medium });
    } catch (error) {
      console.log('Haptics not available:', error);
    }

    console.log('[AR Scanner] Added measurement point:', {
      screenPoint,
      normalizedCoords: { x: normalizedX, y: normalizedY },
      worldCoord,
      canvasDimensions: { width: canvasWidth, height: canvasHeight },
      roomScale: roomScaleMeters,
      perspectiveScale,
      actualRoomSize
    });

    // Add to corners for now (in real implementation, this would use computer vision)
    setScanState(prev => ({
      ...prev,
      detectedFeatures: {
        ...prev.detectedFeatures,
        corners: [...prev.detectedFeatures.corners, scanPoint]
      },
      scanProgress: Math.min(prev.scanProgress + 10, 100)
    }));

    // Update instructions based on progress
    const cornerCount = scanState.detectedFeatures.corners.length + 1;
    if (cornerCount < 4) {
      setScanState(prev => ({
        ...prev,
        currentInstruction: `Point camera at corner ${cornerCount + 1} of the room`
      }));
    } else {
      setScanState(prev => ({
        ...prev,
        currentInstruction: 'Scan walls by slowly moving camera along them'
      }));
    }
  }, [scanState.isScanning, currentScan, scanState.calibrationData.deviceHeight, scanState.calibrationData.roomScale, scanState.detectedFeatures.corners.length]);

  // Process detected features into walls
  const processWalls = useCallback(() => {
    console.log('[AR Scanner] processWalls called:', {
      currentScan: currentScan,
      cornersCount: scanState.detectedFeatures.corners.length,
      corners: scanState.detectedFeatures.corners
    });
    
    if (!currentScan || scanState.detectedFeatures.corners.length < 3) {
      console.log('[AR Scanner] processWalls: Not enough corners or no current scan');
      return;
    }

    const corners = scanState.detectedFeatures.corners;
    const walls: ARWallMeasurement[] = [];

    // Create walls between consecutive corners with improved accuracy
    for (let i = 0; i < corners.length; i++) {
      const startCorner = corners[i];
      const endCorner = corners[(i + 1) % corners.length];
      
      // Calculate wall length in world coordinates (meters)
      const dx = endCorner.worldCoordinate.x - startCorner.worldCoordinate.x;
      const dy = endCorner.worldCoordinate.z - startCorner.worldCoordinate.z;
      const lengthMeters = Math.sqrt(dx * dx + dy * dy);
      
      // Skip walls that are too short (less than 30cm) as they're likely measurement errors
      if (lengthMeters < 0.3) {
        console.log(`[AR Scanner] Skipping wall ${i}: too short (${lengthMeters.toFixed(2)}m)`);
        continue;
      }
      
      // Clamp wall length to reasonable bounds (0.3m to 20m)
      const clampedLength = Math.max(0.3, Math.min(20, lengthMeters));
      
      if (clampedLength !== lengthMeters) {
        console.log(`[AR Scanner] Clamped wall ${i} length from ${lengthMeters.toFixed(2)}m to ${clampedLength.toFixed(2)}m`);
      }

      const wall: ARWallMeasurement = {
        id: uuidv4(),
        startPoint: startCorner,
        endPoint: endCorner,
        length: clampedLength,
        height: 2.4, // Standard ceiling height
        type: 'wall',
        confidence: (startCorner.confidence + endCorner.confidence) / 2
      };

      console.log(`[AR Scanner] Created wall ${i}:`, {
        id: wall.id,
        worldStart: startCorner.worldCoordinate,
        worldEnd: endCorner.worldCoordinate,
        length: `${wall.length.toFixed(2)}m`,
        confidence: wall.confidence.toFixed(3)
      });

      walls.push(wall);
    }

    console.log('[AR Scanner] Generated walls:', walls);
    
    setCurrentScan(prev => prev ? {
      ...prev,
      walls,
      floorArea: calculateFloorArea(corners),
      confidence: walls.reduce((sum, wall) => sum + wall.confidence, 0) / walls.length
    } : null);
  }, [currentScan, scanState.detectedFeatures.corners]);

  // Calculate floor area from corners
  const calculateFloorArea = useCallback((corners: ARScanPoint[]): number => {
    if (corners.length < 3) return 0;

    // Use shoelace formula
    let area = 0;
    for (let i = 0; i < corners.length; i++) {
      const j = (i + 1) % corners.length;
      area += corners[i].worldCoordinate.x * corners[j].worldCoordinate.z;
      area -= corners[j].worldCoordinate.x * corners[i].worldCoordinate.z;
    }
    return Math.abs(area) / 2;
  }, []);

  // Convert AR scan to floor plan walls
  const convertToFloorPlanWalls = useCallback((scan: ARRoomScan): Wall[] => {
    return scan.walls
      .filter(wall => wall.startPoint && wall.endPoint) // Filter walls with valid coordinates
      .map(wall => ({
        start: {
          x: wall.startPoint!.worldCoordinate.x * 50 + 400, // Convert to canvas coordinates
          y: wall.startPoint!.worldCoordinate.z * 50 + 300
        },
        end: {
          x: wall.endPoint!.worldCoordinate.x * 50 + 400,
          y: wall.endPoint!.worldCoordinate.z * 50 + 300
        },
        type: 'None' as const,
        floor: 1
      }));
  }, []);

  // Handle canvas click for adding measurement points
  const handleCanvasClick = useCallback((event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!scanState.isScanning) return;

    const canvas = overlayCanvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const point: Point = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    };

    addMeasurementPoint(point);
  }, [scanState.isScanning, addMeasurementPoint]);

  // Draw overlay graphics
  const drawOverlay = useCallback(() => {
    const canvas = overlayCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw enhanced crosshair
    ctx.strokeStyle = '#00ff00';
    ctx.lineWidth = 3;
    ctx.shadowColor = '#00ff00';
    ctx.shadowBlur = 10;
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    
    ctx.beginPath();
    // Horizontal line
    ctx.moveTo(centerX - 30, centerY);
    ctx.lineTo(centerX - 10, centerY);
    ctx.moveTo(centerX + 10, centerY);
    ctx.lineTo(centerX + 30, centerY);
    // Vertical line
    ctx.moveTo(centerX, centerY - 30);
    ctx.lineTo(centerX, centerY - 10);
    ctx.moveTo(centerX, centerY + 10);
    ctx.lineTo(centerX, centerY + 30);
    ctx.stroke();

    // Draw center dot
    ctx.fillStyle = '#00ff00';
    ctx.shadowBlur = 5;
    ctx.beginPath();
    ctx.arc(centerX, centerY, 3, 0, 2 * Math.PI);
    ctx.fill();

    ctx.shadowBlur = 0; // Reset shadow

    // Draw detected corners with enhanced styling
    scanState.detectedFeatures.corners.forEach((corner, index) => {
      // Outer glow
      ctx.fillStyle = 'rgba(255, 102, 0, 0.3)';
      ctx.beginPath();
      ctx.arc(corner.position.x, corner.position.y, 15, 0, 2 * Math.PI);
      ctx.fill();

      // Main corner circle
      ctx.fillStyle = '#ff6600';
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(corner.position.x, corner.position.y, 10, 0, 2 * Math.PI);
      ctx.fill();
      ctx.stroke();
      
      // Corner number
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 14px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(`${index + 1}`, corner.position.x, corner.position.y);
    });

    // Draw walls between corners with enhanced styling and measurements
    if (scanState.detectedFeatures.corners.length > 1) {
      ctx.strokeStyle = '#00ffff';
      ctx.lineWidth = 4;
      ctx.shadowColor = '#00ffff';
      ctx.shadowBlur = 8;
      ctx.setLineDash([10, 5]);
      ctx.beginPath();
      
      scanState.detectedFeatures.corners.forEach((corner, index) => {
        if (index === 0) {
          ctx.moveTo(corner.position.x, corner.position.y);
        } else {
          ctx.lineTo(corner.position.x, corner.position.y);
          
          // Draw real-time distance measurement for each wall
          const prevCorner = scanState.detectedFeatures.corners[index - 1];
          const dx = corner.worldCoordinate.x - prevCorner.worldCoordinate.x;
          const dy = corner.worldCoordinate.z - prevCorner.worldCoordinate.z;
          const distanceMeters = Math.sqrt(dx * dx + dy * dy);
          
          if (distanceMeters >= 0.1) { // Only show if distance is meaningful
            const midX = (corner.position.x + prevCorner.position.x) / 2;
            const midY = (corner.position.y + prevCorner.position.y) / 2;
            
            // Background for measurement
            ctx.save();
            ctx.setLineDash([]);
            ctx.shadowBlur = 0;
            ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
            ctx.strokeStyle = '#00ffff';
            ctx.lineWidth = 2;
            ctx.fillRect(midX - 35, midY - 12, 70, 24);
            ctx.strokeRect(midX - 35, midY - 12, 70, 24);
            
            // Distance text
            ctx.fillStyle = '#ffffff';
            ctx.font = 'bold 14px Arial';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(`${distanceMeters.toFixed(2)}m`, midX, midY);
            ctx.restore();
          }
        }
      });
      
      // Close the polygon if we have enough corners
      if (scanState.detectedFeatures.corners.length > 2) {
        ctx.closePath();
      }
      ctx.stroke();
      ctx.setLineDash([]); // Reset line dash
      ctx.shadowBlur = 0;
    }

    // Draw measurements if enabled
    if (settings.showMeasurements && currentScan) {
      currentScan.walls
        .filter(wall => wall.startPoint && wall.endPoint) // Only draw measurements for valid walls
        .forEach(wall => {
          const midX = (wall.startPoint!.position.x + wall.endPoint!.position.x) / 2;
          const midY = (wall.startPoint!.position.y + wall.endPoint!.position.y) / 2;
        
        // Background for measurement text
        ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2;
        ctx.fillRect(midX - 40, midY - 15, 80, 30);
        ctx.strokeRect(midX - 40, midY - 15, 80, 30);
        
        // Measurement text
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 12px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        
        const length = settings.measurementUnit === 'metric' 
          ? `${wall.length.toFixed(2)}m`
          : `${(wall.length * 3.28084).toFixed(2)}ft`;
        
        ctx.fillText(length, midX, midY);
      });
    }
  }, [scanState.detectedFeatures, settings.showMeasurements, currentScan, settings.measurementUnit]);

  // Confirm and integrate scan
  const confirmScan = useCallback(() => {
    console.log('[AR Scanner] confirmScan called:', {
      currentScan: currentScan,
      cornersCount: scanState.detectedFeatures.corners.length
    });
    
    if (!currentScan) {
      console.log('[AR Scanner] confirmScan: No current scan');
      return;
    }

    if (scanState.detectedFeatures.corners.length < 3) {
      console.log('[AR Scanner] confirmScan: Not enough corners');
      return;
    }

    console.log('[AR Scanner] Processing walls...');
    
    // Process walls directly instead of relying on state update
    const corners = scanState.detectedFeatures.corners;
    const walls: ARWallMeasurement[] = [];

    // Create walls between consecutive corners with improved accuracy
    for (let i = 0; i < corners.length; i++) {
      const startCorner = corners[i];
      const endCorner = corners[(i + 1) % corners.length];
      
      // Calculate wall length in world coordinates (meters)
      const dx = endCorner.worldCoordinate.x - startCorner.worldCoordinate.x;
      const dy = endCorner.worldCoordinate.z - startCorner.worldCoordinate.z;
      const lengthMeters = Math.sqrt(dx * dx + dy * dy);
      
      // Skip walls that are too short (less than 30cm) as they're likely measurement errors
      if (lengthMeters < 0.3) {
        console.log(`[AR Scanner] Skipping wall ${i}: too short (${lengthMeters.toFixed(2)}m)`);
        continue;
      }
      
      // Clamp wall length to reasonable bounds (0.3m to 20m)
      const clampedLength = Math.max(0.3, Math.min(20, lengthMeters));
      
      if (clampedLength !== lengthMeters) {
        console.log(`[AR Scanner] Clamped wall ${i} length from ${lengthMeters.toFixed(2)}m to ${clampedLength.toFixed(2)}m`);
      }

      const wall: ARWallMeasurement = {
        id: uuidv4(),
        startPoint: startCorner,
        endPoint: endCorner,
        length: clampedLength,
        height: 2.4, // Standard ceiling height
        type: 'wall',
        confidence: (startCorner.confidence + endCorner.confidence) / 2
      };

      console.log(`[AR Scanner] Created wall ${i}:`, {
        id: wall.id,
        worldStart: startCorner.worldCoordinate,
        worldEnd: endCorner.worldCoordinate,
        length: `${wall.length.toFixed(2)}m`,
        confidence: wall.confidence.toFixed(3)
      });

      walls.push(wall);
    }

    console.log('[AR Scanner] Generated walls:', walls);

    // Create updated scan with walls
    const updatedScan: ARRoomScan = {
      ...currentScan,
      walls,
      corners,
      floorArea: calculateFloorArea(corners),
      confidence: walls.reduce((sum, wall) => sum + wall.confidence, 0) / walls.length
    };

    console.log('[AR Scanner] Updated scan with walls:', updatedScan);
    
    // Update the current scan state
    setCurrentScan(updatedScan);
    
    console.log('[AR Scanner] Calling onScanComplete...');
    onScanComplete(updatedScan);
    
    console.log('[AR Scanner] Calling onIntegrateToFloorPlan...');
    onIntegrateToFloorPlan(updatedScan);
  }, [currentScan, scanState.detectedFeatures.corners, calculateFloorArea, onScanComplete, onIntegrateToFloorPlan]);

  // Initialize on component mount
  useEffect(() => {
    initializeCamera();
    getDeviceInfo();
    
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, [initializeCamera, getDeviceInfo]);

  // Update overlay
  useEffect(() => {
    if (settings.enableRealTimeOverlay) {
      drawOverlay();
    }
  }, [settings.enableRealTimeOverlay, drawOverlay]);

  return (
    <div className="fixed inset-0 bg-black z-[100] flex flex-col">
      {/* Header - More Compact */}
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white px-4 py-2 shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-1.5 bg-blue-500 rounded-full">
              <CameraIcon className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-base font-bold">AR Room Scanner</h2>
              <p className="text-xs text-gray-300 opacity-90">{scanState.currentInstruction}</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-1.5 hover:bg-gray-700 rounded-full transition-colors duration-200"
            aria-label="Close Scanner"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Progress Bar - More Compact */}
      {scanState.isScanning && (
        <div className="bg-gray-800 px-4 py-2 border-b border-gray-700">
          <div className="flex items-center justify-between mb-1">
            <span className="text-white text-xs font-medium">Scanning</span>
            <span className="text-blue-400 text-xs font-bold">{scanState.scanProgress}%</span>
          </div>
          <div className="w-full bg-gray-700 rounded-full h-1.5 mb-1">
            <div 
              className="bg-gradient-to-r from-blue-500 to-purple-500 h-1.5 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${scanState.scanProgress}%` }}
            />
          </div>
          <div className="flex items-center justify-between text-xs text-gray-400">
            <span>Corners: {scanState.detectedFeatures.corners.length}</span>
            <span>Tap corners</span>
          </div>
        </div>
      )}

      {/* Camera View - Maximized */}
      <div className="flex-1 relative bg-black">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="w-full h-full object-cover"
        />
        
        {/* Overlay Canvas */}
        <canvas
          ref={overlayCanvasRef}
          width={800}
          height={600}
          className="absolute inset-0 w-full h-full cursor-crosshair"
          onClick={handleCanvasClick}
        />

        {/* Floating Instructions - More Compact */}
        {!scanState.isScanning && (
          <div className="absolute top-2 left-2 right-2">
            <div className="bg-black bg-opacity-70 backdrop-blur-sm rounded-lg p-3 text-white">
              <div className="flex items-center space-x-2">
                <div className="p-1 bg-blue-500 rounded-full">
                  <Target className="w-3 h-3" />
                </div>
                <div>
                  <p className="text-sm font-semibold">Ready to Scan</p>
                  <p className="text-xs text-gray-300">Tap "Start Scan" and point at corners</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Scanning Stats Overlay - More Compact */}
        {scanState.isScanning && scanState.detectedFeatures.corners.length > 0 && (
          <div className="absolute top-2 right-2">
            <div className="bg-black bg-opacity-70 backdrop-blur-sm rounded-lg p-2 text-white min-w-[80px]">
              <div className="text-center">
                <div className="text-xl font-bold text-blue-400">{scanState.detectedFeatures.corners.length}</div>
                <div className="text-xs text-gray-300">Corners</div>
              </div>
            </div>
          </div>
        )}

        {/* Real-time Scan Statistics */}
        <div className="absolute top-20 left-4 bg-black bg-opacity-60 backdrop-blur-sm text-white p-3 rounded-lg">
          <div className="text-xs font-medium space-y-1">
            <div>Corners: {scanState.detectedFeatures.corners.length}</div>
            <div>Progress: {scanState.scanProgress}%</div>
            {scanState.detectedFeatures.corners.length >= 3 && (
              <div>
                Est. Area: {calculateFloorArea(scanState.detectedFeatures.corners).toFixed(1)}m²
              </div>
            )}
          </div>
        </div>

        {/* Calibration Modal - Enhanced */}
        {isCalibrating && (
          <div className="absolute inset-0 bg-black bg-opacity-75 backdrop-blur-sm flex items-center justify-center px-4 py-6">
            <div className="bg-white rounded-2xl px-4 sm:px-6 py-6 max-w-sm w-full shadow-2xl">
              <div className="text-center mb-4">
                <div className="p-3 bg-blue-100 rounded-full w-fit mx-auto mb-3">
                  <Target className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Calibrate Scanner</h3>
                <p className="text-gray-600 text-sm">Set your device height for accurate measurements</p>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Device Height (meters)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    min="0.5"
                    max="3.0"
                    value={scanState.calibrationData.deviceHeight}
                    onChange={(e) => setScanState(prev => ({
                      ...prev,
                      calibrationData: {
                        ...prev.calibrationData,
                        deviceHeight: parseFloat(e.target.value)
                      }
                    }))}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">Typical height: 1.5m (standing)</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Estimated Room Size (meters)
                  </label>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Room Width (meters)</label>
                      <select
                        value={Math.round(scanState.calibrationData.roomScale / 100 * 6)}
                        onChange={(e) => {
                          const roomWidth = parseInt(e.target.value);
                          setScanState(prev => ({
                            ...prev,
                            calibrationData: {
                              ...prev.calibrationData,
                              roomScale: (roomWidth / 6) * 100
                            }
                          }));
                        }}
                        className="w-full p-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="2">Small (2m) - Bathroom</option>
                        <option value="3">Medium (3m) - Bedroom</option>
                        <option value="4">Large (4m) - Living Room</option>
                        <option value="5">Very Large (5m) - Large Room</option>
                        <option value="6">Extra Large (6m+) - Hall/Garage</option>
                        <option value="8">Huge (8m+) - Warehouse</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Expected Wall Distance</label>
                      <select
                        onChange={(e) => {
                          const expectedDistance = parseFloat(e.target.value);
                          // Adjust room scale based on expected wall distances
                          setScanState(prev => ({
                            ...prev,
                            calibrationData: {
                              ...prev.calibrationData,
                              roomScale: (expectedDistance / 3) * 100 // Scale relative to 3m baseline
                            }
                          }));
                        }}
                        className="w-full p-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select typical wall distance...</option>
                        <option value="2">2m - Small room walls</option>
                        <option value="3">3m - Medium room walls</option>
                        <option value="4">4m - Large room walls</option>
                        <option value="5">5m - Very large room walls</option>
                        <option value="6">6m+ - Extra large spaces</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">How far apart are the corners you'll tap?</p>
                    </div>
                  </div>
                </div>
                
                <button
                  onClick={() => setIsCalibrating(false)}
                  className="w-full px-4 py-3 bg-blue-500 text-white font-semibold rounded-lg hover:bg-blue-600 transition-colors duration-200"
                >
                  Save Settings
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bottom Control Panel - Moved Higher */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-gray-900 to-gray-800 text-white">
          {/* Main Controls - More Compact */}
          <div className="px-4 py-3">
            <div className="flex items-center justify-center space-x-3">
              {!scanState.isScanning ? (
                <button
                  onClick={startScanning}
                  className="flex items-center space-x-2 px-5 py-2.5 bg-gradient-to-r from-green-500 to-green-600 rounded-full hover:from-green-600 hover:to-green-700 transition-all duration-200 shadow-lg font-semibold"
                >
                  <Play className="w-4 h-4" />
                  <span>Start Scan</span>
                </button>
              ) : (
                <button
                  onClick={stopScanning}
                  className="flex items-center space-x-2 px-5 py-2.5 bg-gradient-to-r from-red-500 to-red-600 rounded-full hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-lg font-semibold"
                >
                  <Square className="w-4 h-4" />
                  <span>Stop Scan</span>
                </button>
              )}

              <button
                onClick={() => setIsCalibrating(true)}
                className="flex items-center space-x-1 px-3 py-2.5 bg-gray-700 rounded-full hover:bg-gray-600 transition-colors duration-200"
              >
                <Target className="w-4 h-4" />
                <span className="hidden sm:inline text-sm">Calibrate</span>
              </button>

              {currentScan && scanState.detectedFeatures.corners.length >= 3 && (
                <button
                  onClick={confirmScan}
                  className="flex items-center space-x-2 px-5 py-2.5 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full hover:from-blue-600 hover:to-purple-600 transition-all duration-200 shadow-lg font-semibold animate-pulse"
                >
                  <CheckCircle className="w-4 h-4" />
                  <span>Confirm</span>
                </button>
              )}
            </div>
          </div>

          {/* Secondary Controls - More Compact */}
          <div className="px-4 py-2 border-t border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <label className="flex items-center space-x-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.showMeasurements}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      showMeasurements: e.target.checked
                    }))}
                    className="w-3 h-3 text-blue-500 border-gray-600 rounded focus:ring-blue-500 bg-gray-700"
                  />
                  <span className="text-xs text-gray-300">Measurements</span>
                </label>
                
                <label className="flex items-center space-x-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.enableRealTimeOverlay}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      enableRealTimeOverlay: e.target.checked
                    }))}
                    className="w-3 h-3 text-blue-500 border-gray-600 rounded focus:ring-blue-500 bg-gray-700"
                  />
                  <span className="text-xs text-gray-300">Overlay</span>
                </label>
              </div>

              <button
                onClick={() => {
                  setScanState(prev => ({
                    ...prev,
                    detectedFeatures: {
                      walls: [],
                      corners: [],
                      doors: [],
                      windows: []
                    },
                    scanProgress: 0
                  }));
                  setCurrentScan(null);
                }}
                className="flex items-center space-x-1 px-2 py-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors duration-200"
              >
                <RotateCcw className="w-3 h-3" />
                <span className="text-xs">Reset</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ARRoomScanner; 